import { jsx as _jsx } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import noop from 'lodash/fp/noop';
import BottomSheet from './BottomSheet';
import useTimeout from '../../hooks/useTimeout';
import useLocalStorage from '../../useLocalStorage';
const DEFAULT_SHOW_AFTER = 0; // immediately after mount
const DEFAULT_HIDE_AFTER = 3_600_000; // after 1 hour
const sanitizeFeatureName = (value) => `${value.charAt(0).toUpperCase()}${value.slice(1)}`.replaceAll(' ', '');
/**
 * A wrapper component for the BottomSheet that allows to control it's visibility via timers and to use
 * the localStorage to save user interaction.
 *
 * Don't forget to cleanup the localStorage for the user when removing a feature
 * by setting "cleanupLocalStorage" to "true" and deploy it like this.
 * The cleanup will remove the localStorage flag on mount and will not show the bottom sheet.
 *
 * @example
    const [isDismissed, setIsDismissed] = useState(false);

    const handleClose = () => { // If needed trigger Google Analytics here };

    return (
        <TimedBottomSheet
            dismissed={isDismissed}
            featureName='dummyBottomSheet'
            showAfter={1_000}
            hideAfter={15_000}
            className='max-width-500'
            onClose={handleClose}
        >
            <div className='display-flex align-items-center gap-20'>
                Lorem ipsum dolor sit amet
                <Button bsStyle={Button.PRIMARY} onClick={() => setIsDismissed(true)}>
                    Got it
                </Button>
            </div>
        </TimedBottomSheet>
    );
 */
export const TimedBottomSheet = (props) => {
    const { dismissed = false, featureName, showAfter = DEFAULT_SHOW_AFTER, hideAfter = DEFAULT_HIDE_AFTER, alwaysOn = false, showCloseButton = true, width, cleanupLocalStorage = false, onClose = noop, bodyClassName = 'padding-25 margin-right-25', localStoragePrefix = '', className, children, ...remainingProps } = props;
    const [showBottomSheet, setShowBottomSheet] = useState(false);
    const [isHiddenByUser, setIsHiddenByUser, removeIsHiddenFlag] = useLocalStorage(`${localStoragePrefix}hide${sanitizeFeatureName(featureName)}`, false);
    // In case the visibility is controlled by the outside, means the user clicked on the
    // content of the bottom sheet, close the sheet and set the localStorage flag for not
    // showing it again
    const [prevDismissed, setPrevDismissed] = useState(dismissed);
    if (dismissed && dismissed !== prevDismissed) {
        setPrevDismissed(dismissed);
        setIsHiddenByUser(true);
        setShowBottomSheet(false);
    }
    // Show the bottom sheet automatically after a given amount of time
    useTimeout(() => {
        if (!dismissed && !isHiddenByUser && !cleanupLocalStorage) {
            setShowBottomSheet(true);
        }
    }, showAfter);
    // Hide the bottom sheet automatically after a given amount of time
    useTimeout(() => setShowBottomSheet(alwaysOn ? true : false), showAfter + hideAfter);
    // Cleanup functionality. The cleanup will remove the localStorage flag on mount
    // and will not show the bottom sheet.
    useEffect(() => {
        if (cleanupLocalStorage) {
            removeIsHiddenFlag();
        }
    }, []);
    const handleCloseBottomSheet = () => {
        setShowBottomSheet(false);
        setIsHiddenByUser(true);
        onClose();
    };
    return (_jsx("div", { ...remainingProps, children: _jsx(BottomSheet, { show: showBottomSheet, width: width, detach: true, showCloseButton: showCloseButton, onClose: handleCloseBottomSheet, className: className, bodyClassName: bodyClassName, children: children }) }));
};
export default TimedBottomSheet;
